import Autocomplete from '@material-ui/core/Autocomplete';
import ButtonBase from '@material-ui/core/ButtonBase';
import InputBase from '@material-ui/core/InputBase';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import {
  InputSearchIcon,
  Option,
  PopperWrapperStyled,
  SearchWrapper,
  useResidentListingStyles,
} from 'components/ResidentHeader/components/ResidentListing/styles';
import routePaths from 'constants/route-paths';
import React, { SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { matchPath, useLocation } from 'react-router-dom';
import { NursingHomeHeadPropType, ResidentHeadPropType } from 'services/residenHead';
import { getPath } from 'utils/path';

type ResidentListingrProps = {
  nursingHead: NursingHomeHeadPropType | undefined;
  residentHead: ResidentHeadPropType | undefined;
};
type AutocompleteCloseReasonProps = 'toggleInput' | 'escape' | string;

const ResidentListing: React.VFC<ResidentListingrProps> = ({ nursingHead, residentHead }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useResidentListingStyles();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingValue, setPendingValue] = useState<ResidentHeadPropType[]>([]);

  const path = matchPath(location.pathname, {
    path: routePaths.GROUP + routePaths.RESIDENT,
  }) as { params: { residentId: string; groupId: string } };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setPendingValue([]);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (
    event: SyntheticEvent<Element, Event>,
    reason: AutocompleteCloseReasonProps,
  ) => {
    if (reason === 'toggleInput') {
      return false;
    }
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
    return true;
  };

  const handleClickResident = (groupId: string, residentId: string) => {
    const params = {
      residentId: path?.params?.residentId,
      groupId: path?.params?.groupId,
    } as { groupId: string; residentId: string };
    const currentPage = location ? location?.pathname?.split(params.residentId)[1] : '';
    history.push({
      pathname: getPath([routePaths.GROUP, routePaths.RESIDENT, currentPage], {
        groupId,
        residentId,
      }),
      search: location.search,
    });
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'github-label' : undefined;

  return residentHead && nursingHead ? (
    <>
      <div className={classes.root}>
        <ButtonBase
          disableRipple
          className={classes.button}
          aria-describedby={id}
          onClick={handleClick}
        >
          <h4>
            <span>
              {residentHead.name.last}, {residentHead.name.firstFull}
            </span>
          </h4>
          <ArrowDropDownIcon fontSize="large" />
        </ButtonBase>
      </div>
      <PopperWrapperStyled
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        className={classes.popper}
      >
        {
          <Autocomplete
            open
            onClose={handleClose}
            multiple
            classes={{
              paper: classes.paper,
              option: classes.option,
              popperDisablePortal: classes.popperDisablePortal,
            }}
            value={pendingValue}
            onChange={(event, newValue: ResidentHeadPropType[]) => {
              setPendingValue(newValue);
            }}
            disableCloseOnSelect
            disablePortal
            noOptionsText={t('noMatchFound')}
            renderOption={(
              props: React.HTMLAttributes<HTMLLIElement>,
              option: ResidentHeadPropType,
            ) =>
              option && option.name && option.id ? (
                <Option
                  key={option.id}
                  onClick={() => handleClickResident(nursingHead.id, option.id)}
                >
                  <div>
                    {option?.name?.last}, {option?.name?.firstFull}
                  </div>
                </Option>
              ) : null
            }
            options={[...nursingHead.residents]}
            getOptionLabel={(option) => `${option.name.firstFull} ${option.name.last}`}
            renderInput={(params) => (
              <SearchWrapper>
                <InputBase
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  className={classes.inputBase}
                />
                <InputSearchIcon />
              </SearchWrapper>
            )}
          />
        }
      </PopperWrapperStyled>
    </>
  ) : (
    <div></div>
  );
};
export default ResidentListing;
